/*
  Free-shipping progress bar.

  Seeded from the server-rendered cart, then refreshed from /cart.js on every Dawn cartUpdate.
  The event payload is not used: product-form.js publishes the added line item, not the cart, so
  only a re-fetch gives a trustworthy total.
*/
if (!customElements.get('tv-free-shipping-bar')) {
  const DISMISS_KEY = 'tvFreeShippingBarDismissed';

  class TvFreeShippingBar extends HTMLElement {
    connectedCallback() {
      // Same trap as the size chart tab: any ancestor carrying a transform, even an identity one,
      // becomes the containing block for position: fixed and drags the bar off the viewport. The
      // move re-enters connectedCallback, so binding waits until the element is in place.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      if (this.bound) return;
      this.bound = true;

      this.message = this.querySelector('[data-tv-fsb-message]');
      this.track = this.querySelector('[data-tv-fsb-track]');
      this.fill = this.querySelector('[data-tv-fsb-fill]');

      this.querySelector('[data-tv-fsb-close]').addEventListener('click', () => {
        try {
          sessionStorage.setItem(DISMISS_KEY, 'true');
        } catch (e) {}
        this.setVisible(false);
      });

      this.resizeObserver = new ResizeObserver(() => this.syncBodyOffset());
      this.resizeObserver.observe(this);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
      // A page restored from the back/forward cache still shows the cart as it was when the
      // shopper left it, typically before a change made in checkout or another tab.
      window.addEventListener('pageshow', (event) => event.persisted && this.refresh());

      this.render(Number(this.dataset.total), Number(this.dataset.itemCount));
    }

    disconnectedCallback() {
      // The reparent above disconnects once on purpose; only tear down a bound instance.
      if (!this.message) return;
      this.unsubscribe?.();
      this.resizeObserver?.disconnect();
    }

    async refresh() {
      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.render(cart.total_price, cart.item_count);
      } catch (e) {}
    }

    render(total, itemCount) {
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      const threshold = Math.round(Number(this.dataset.threshold) * rate);
      if (!threshold || Number.isNaN(total)) return this.setVisible(false);

      const remaining = Math.max(threshold - total, 0);
      const percent = Math.min(Math.round((total / threshold) * 100), 100);

      this.message.textContent =
        remaining === 0
          ? this.dataset.messageUnlocked
          : this.dataset.messageRemaining.replace('[amount]', this.formatMoney(remaining));
      this.fill.style.width = `${percent}%`;
      this.track.setAttribute('aria-valuenow', percent);
      this.classList.toggle('tv-free-shipping-bar--unlocked', remaining === 0);

      this.setVisible(itemCount > 0 && !this.isDismissed());
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
        }).format(cents / 100);
      } catch (e) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    isDismissed() {
      try {
        return sessionStorage.getItem(DISMISS_KEY) === 'true';
      } catch (e) {
        return false;
      }
    }

    setVisible(visible) {
      this.hidden = !visible;
      document.body.classList.toggle('tv-free-shipping-bar-visible', visible);
      this.syncBodyOffset();
    }

    // The bar floats over the bottom of the page; reserving its height keeps the footer's last
    // line (payment icons, copyright) reachable instead of permanently underneath it.
    syncBodyOffset() {
      document.body.style.setProperty('--tv-fsb-height', `${this.hidden ? 0 : this.offsetHeight}px`);
    }
  }

  customElements.define('tv-free-shipping-bar', TvFreeShippingBar);
}
